import { distanceLessThanOrEqual, Line2D, LineSegment2D, Polygon2D } from '@paragon/geometry-library';
import { notNullish } from '@paragon/utilities';

export function splitTrussEnvelopePolygon(
  polygon: Polygon2D,
  envelopeCenterLine: Line2D,
  splittingLines: readonly [Line2D, Line2D],
): [LineSegment2D[], LineSegment2D[]] {
  if (splittingLines.length !== 2) {
    throw new Error('Invalid usage of `splitTrussEnvelopePolygon` -- exactly two splitting lines must be provided.');
  }

  const [splittingLineA, splittingLineB] = splittingLines;

  if (splittingLineA.equals(splittingLineB)) {
    throw new Error('Invalid usage of `splitTrussEnvelopePolygon` -- splitting lines must not be coincident.');
  }

  if (!splittingLineA.isParallelTo(splittingLineB)) {
    throw new Error('Invalid usage of `splitTrussEnvelopePolygon` -- splitting lines must be parallel to each other.');
  }

  const mainSegments = [] as LineSegment2D[];
  const splitOffSegments = [] as LineSegment2D[];

  const centerReferencePoint = new LineSegment2D(
    splittingLines[0].intersectWithLine(envelopeCenterLine)!,
    splittingLines[1].intersectWithLine(envelopeCenterLine)!,
  ).midpoint;

  const distanceFromCenterToSplit = centerReferencePoint.distanceTo(
    envelopeCenterLine.intersectWithLine(splittingLines[0])!,
  );

  for (const edge of polygon.edges) {
    const intersections = splittingLines.map((line) => edge.intersectWithLine(line)).filter(notNullish);

    if (intersections.length > 0) {
      const splitSegments = intersections.reduce(
        (edges, intersection) => edges.flatMap((edge) => edge.splitAtPoint(intersection)),
        [edge],
      );

      for (const segment of splitSegments) {
        const distanceFromCenter = centerReferencePoint.distanceTo(segment.midpoint);
        if (distanceLessThanOrEqual(distanceFromCenter, distanceFromCenterToSplit)) mainSegments.push(segment);
        else splitOffSegments.push(segment);
      }
    } else {
      const distanceFromCenter = centerReferencePoint.distanceTo(edge.midpoint);
      if (distanceLessThanOrEqual(distanceFromCenter, distanceFromCenterToSplit)) mainSegments.push(edge);
      else splitOffSegments.push(edge);
    }
  }

  return [mainSegments, splitOffSegments];
}
